import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppSelector from "@/core/helpers/useAppSelector";
import { BankCardIcon, CashIcon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import OfferCheckbox from "@/shared/widgets/OfferCheckbox";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerResolverSelector } from "@/domains/offer/slice";
import useOfferResolver from "@/domains/offer/hooks/useOfferResolver";
import useOfferResolverVehicle from "@/domains/offer/hooks/useOfferResolverVehicle";
import OfferVehicleInfo from "@/domains/offer/components/vehicle/OfferVehicleInfo";
import OfferVehicleUpgrade from "@/domains/offer/components/vehicle/OfferVehicleUpgrade";
import OfferVehicleViewContent from "@/domains/offer/components/vehicle/OfferVehicleViewContent";
import VehicleOfferBuyCost from "@/domains/offer/components/vehicle/VehicleOfferBuyCost";
import VehicleOfferUpgradeButton from "@/domains/offer/components/vehicle/VehicleOfferUpgradeButton";

export default function OfferEstateVehicleResolverContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");
  const { vehicle, pending } = useOfferResolverVehicle(isShow);
  const { pending: isLoading, resolveByCard, resolveByCash, reject } = useOfferResolver();
  const offerResolver = useAppSelector(offerResolverSelector);

  const [isAgree, setIsAgree] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);

  const handleCard = useCallback(() => {
    resolveByCard();
  }, [resolveByCard]);

  const handleCash = useCallback(() => {
    resolveByCash();
  }, [resolveByCash]);

  const handleReject = useCallback(() => {
    reject();
  }, [reject]);

  const upgradeCount = vehicle?.upgrade
    ? Object.values(vehicle?.upgrade).filter((item) => !!item).length
    : 0;

  const toggleAgree = useCallback(() => {
    setIsAgree((prev) => !prev);
  }, []);

  const openUpgrade = useCallback(() => setIsUpgradeOpen(true), []);
  const closeUpgrade = useCallback(() => setIsUpgradeOpen(false), []);

  useEffect(() => {
    if (!isShow) {
      setIsAgree(false);
      setIsUpgradeOpen(false);
    }
  }, [isShow]);

  const footer = (
    <div className="grid grid-cols-2 gap-2">
      <Button
        className="h-[2.75rem]"
        onClick={handleCash}
        disabled={isLoading || pending || !isAgree}
        loading={isLoading || pending}
      >
        <CashIcon className="w-5.5 mr-2.5" />
        {t("offer:offer.buy")}
      </Button>
      <Button
        className="h-[2.75rem]"
        onClick={handleCard}
        disabled={isLoading || pending || !isAgree}
        loading={isLoading || pending}
      >
        <BankCardIcon className="w-5 mr-2.5" />
        {t("offer:offer.buy")}
      </Button>
    </div>
  );

  return (
    <Modal
      open={isShow}
      title={t("offer.vehicle.creator.title")}
      onClose={handleReject}
      footer={footer}
      style={{ fontFamily: "TT Norms Pro" }}
    >
      <div className="flex flex-col">
        <OfferPlayerInfo
          text={t("offer.buyer")}
          id={offerResolver?.baseCharacter?.id}
          name={offerResolver?.baseCharacter?.name}
          avatar={offerResolver?.baseCharacter?.avatar}
        />

        <p className="mt-4 mb-3.5 text-16 font-medium">{t("offer.vehicle.resolver.vehicle")}:</p>

        <OfferView isLoading={pending}>
          <OfferVehicleViewContent selectedVehicle={vehicle} />
        </OfferView>

        <OfferVehicleInfo
          vehicle={{
            plateNumber: vehicle?.plateNumber,
            mileage: vehicle?.mileage,
            condition: vehicle?.condition,
          }}
        />

        <VehicleOfferUpgradeButton count={upgradeCount} onClick={openUpgrade} />

        <VehicleOfferBuyCost cost={offerResolver?.price} />

        <OfferCheckbox
          isChecked={isAgree}
          onChange={toggleAgree}
          label={t("offer.vehicle.resolver.agree")}
        />

        {isUpgradeOpen && vehicle?.upgrade && (
          <OfferVehicleUpgrade
            upgrade={vehicle?.upgrade}
            vin={vehicle?.vinNumber}
            maxInventoryWeight={vehicle?.maxInventoryWeight}
            onClose={closeUpgrade}
          />
        )}
      </div>
    </Modal>
  );
}
